import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { ContactFooter } from "@/components/ContactFooter";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [weakBlock, setWeakBlock] = useState(""); // blocco debole dal test (solo display)
  const attributionRef = useRef({}); // campi utm_* + blocco, letti una volta al mount
  const redirectSampleRef = useRef(false); // utm_medium === 'test' → mini-simulazione
  const parsedRef = useRef(false); // garantisce il parsing una sola volta (StrictMode-safe)

  // Legge nome/email/blocco dal frammento (#...) e gli utm_* dalla query string.
  // Subito dopo rimuove il frammento dalla barra indirizzi (privacy), mantenendo la query.
  useEffect(() => {
    if (parsedRef.current) return; // evita doppio parsing (es. StrictMode) dopo lo strip hash
    parsedRef.current = true;
    try {
      const query = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));

      const nomeH = hash.get("nome");
      const emailH = hash.get("email");
      const bloccoH = hash.get("blocco");
      if (nomeH) setNome(nomeH);
      if (emailH) setEmail(emailH);
      if (bloccoH) setWeakBlock(bloccoH);

      const attr = {};
      const map = {
        signupSource: query.get("utm_source"),
        signupMedium: query.get("utm_medium"),
        signupCampaign: query.get("utm_campaign"),
        signupContent: query.get("utm_content"),
        signupChannel: query.get("utm_term"),
        testWeakBlock: bloccoH,
      };
      Object.entries(map).forEach(([k, v]) => {
        if (v) attr[k] = v;
      });
      attributionRef.current = attr;
      redirectSampleRef.current = query.get("utm_medium") === "test";

      // Rimuove SOLO il frammento (nome/email non restano in barra indirizzi)
      if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    } catch (_e) {
      /* no-op: in assenza di parametri la pagina resta vuota */
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa privacy per registrarti");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, nome, consent, attributionRef.current);
      // Nota: /register è avvolto da PublicOnly, che al set dell'utente reindirizza a "/".
      // Per il caso "dal test" usiamo un redirect hard verso la mini-simulazione, così non
      // viene sovrascritto dal redirect automatico. Il caso standard resta invariato (home).
      if (redirectSampleRef.current) {
        window.location.replace("/esame?mode=sample");
      } else {
        navigate("/");
      }
    } catch (err) {
      toast.error(errMsg(err, "Registrazione non riuscita"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-5 pt-[14vh] fade-up">
        <div className="flex items-center gap-2 mb-10">
          <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--volt)] border border-black/10" aria-hidden="true" />
          <span className="font-extrabold tracking-tight uppercase">Propedeutico</span>
        </div>
        <h1 className="text-4xl font-extrabold tracking-tight leading-tight">Crea il tuo account.</h1>
        <p className="mt-3 text-[color:var(--text-2)] leading-6">I tuoi progressi restano salvati su ogni dispositivo.</p>

        {weakBlock && (
          <div
            className="mt-5 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--bg-subtle)] px-4 py-3"
            data-testid="register-weakblock-note"
          >
            <p className="text-sm text-[color:var(--text-2)] leading-6">
              Dal test: il blocco da rinforzare è <strong className="text-[color:var(--ink)]">{weakBlock}</strong>. Dopo la registrazione parti dalla mini-simulazione gratuita.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="nome" className="block text-sm font-semibold mb-1.5">Nome</label>
            <input
              id="nome"
              type="text"
              value={nome}
              autoComplete="name"
              onChange={(e) => setNome(e.target.value)}
              data-testid="register-nome-input"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
              placeholder="Il tuo nome"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-semibold mb-1.5">Email</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
              placeholder="nome@esempio.it"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-semibold mb-1.5">Password</label>
            <input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
              placeholder="Minimo 6 caratteri"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="register-privacy-checkbox"
              className="mt-0.5 w-4 h-4 accent-black shrink-0"
            />
            <span className="text-xs text-[color:var(--text-2)] leading-5">
              Ho letto e accetto l'<strong className="text-[color:var(--ink)]">informativa privacy</strong>: i miei dati (email, nome, progressi di studio e risultati dei quiz) sono usati solo per il funzionamento dell'app e posso chiederne la cancellazione in ogni momento.
            </span>
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="press w-full rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
          >
            {busy ? "Creazione account…" : "Registrati"}
          </button>
        </form>

        <p className="mt-6 text-sm text-[color:var(--text-2)]">
          Hai già un account?{" "}
          <Link to="/login" className="font-bold text-[color:var(--ink)] underline underline-offset-2" data-testid="register-goto-login-link">
            Accedi
          </Link>
        </p>

        <ContactFooter className="pb-10" />
      </div>
    </div>
  );
}
